import { rfqDisplayLabel } from '../api/rfqLabels'
import type { RfqDto } from '../api/types'
import { DeliveryBlock, LineList } from './OrdersDetailShared'
import { StatusPill } from '../components/StatusPill'
import {
  activeLines,
  formatDate,
  formatRejectionReason,
  formatStatus,
  restaurantSupplierDecision,
} from './ordersUtils'

type RestaurantOrderDetailPanelProps = {
  rfq: RfqDto
  error: string | null
  actionLoading: boolean
  excludeMode: boolean
  excludedLineIds: number[]
  disputeOpen: boolean
  disputeComment: string
  onBack: () => void
  onToggleExcludedLine: (lineId: number) => void
  onAcceptSubstitution: () => void
  onStartExcludeMode: () => void
  onCancelExcludeMode: () => void
  onExcludeLines: () => void
  onCancelSubstitution: () => void
  onConfirmCompletion: () => void
  onStartDispute: () => void
  onCancelDispute: () => void
  onDisputeCommentChange: (value: string) => void
  onSubmitDispute: () => void
  onOpenChat: () => void
  onCancelOrder: () => void
}

export const RestaurantOrderDetailPanel = ({
  rfq,
  error,
  actionLoading,
  excludeMode,
  excludedLineIds,
  disputeOpen,
  disputeComment,
  onBack,
  onToggleExcludedLine,
  onAcceptSubstitution,
  onStartExcludeMode,
  onCancelExcludeMode,
  onExcludeLines,
  onCancelSubstitution,
  onConfirmCompletion,
  onStartDispute,
  onCancelDispute,
  onDisputeCommentChange,
  onSubmitDispute,
  onOpenChat,
  onCancelOrder,
}: RestaurantOrderDetailPanelProps) => {
  const decision = restaurantSupplierDecision(rfq)
  const canCancel = (rfq.status === 'open' || rfq.status === 'draft') && (!decision || decision === 'pending')
  const awaitingConfirmation = decision === 'awaiting_customer_confirmation'
  const substitutionProposed = decision === 'substitution_proposed'
  const displayStatus = awaitingConfirmation
    ? 'awaiting_customer_confirmation'
    : substitutionProposed
      ? 'substitution_proposed'
      : rfq.status
  const rejectionLabel = formatRejectionReason(rfq.rejectionReasonCode)
  const rfqActiveLines = activeLines(rfq.lines)

  return (
    <>
      <button type="button" className="echat-back echat-back--orders-mobile" onClick={onBack}>
        ← К списку
      </button>

      <section aria-label={`Заявка ${rfq.id}`} className="echat-orders echat-orders--detail">
        <article className="echat-panel">
          <StatusPill
            status={displayStatus}
            label={formatStatus(substitutionProposed ? decision : awaitingConfirmation ? decision : rfq.status)}
          />
          <h2 className="echat-panel__title">{rfqDisplayLabel(rfq)}</h2>
          {rfq.description ? <p className="echat-panel__text">{rfq.description}</p> : null}
          <p className="echat-panel__meta">Создана: {formatDate(rfq.createdAt)}</p>
          {rfq.supplierContact?.supplierName ? (
            <p className="echat-panel__text">Поставщик: {rfq.supplierContact.supplierName}</p>
          ) : null}
          <DeliveryBlock
            date={rfq.desiredDeliveryDate}
            timeUntil={rfq.desiredDeliveryTimeUntil}
            address={rfq.deliveryAddress}
          />
          {rejectionLabel ? <p className="echat-panel__text">Причина отказа: {rejectionLabel}</p> : null}
          {rfq.customerDisputeComment ? (
            <p className="echat-panel__text">Ваши замечания: {rfq.customerDisputeComment}</p>
          ) : null}
          {rfq.substitutionComment ? (
            <p className="echat-panel__text">Предложение замены: {rfq.substitutionComment}</p>
          ) : null}
          {excludeMode ? (
            <>
              <p className="echat-panel__text">Выберите позиции, которые нужно исключить из заявки</p>
              <LineList lines={rfqActiveLines} selectable selectedIds={excludedLineIds} onToggle={onToggleExcludedLine} />
            </>
          ) : (
            <LineList lines={rfq.lines} />
          )}
        </article>

        {substitutionProposed && !excludeMode ? (
          <article className="echat-panel echat-panel--highlight">
            <h3 className="echat-panel__subtitle">Поставщик предложил замену</h3>
            <p className="echat-panel__text">
              Примите замену целиком, исключите отдельные позиции или откажитесь от заявки.
            </p>
          </article>
        ) : null}

        {awaitingConfirmation ? (
          <article className="echat-panel echat-panel--highlight">
            <h3 className="echat-panel__subtitle">Поставщик отметил заявку выполненной</h3>
            <p className="echat-panel__text">
              Проверьте, что заказ доставлен полностью и в согласованном объёме. Если всё в порядке —
              подтвердите выполнение. Если есть расхождения — опишите замечания, и поставщик свяжется с вами
              для уточнения.
            </p>
            {disputeOpen ? (
              <label className="echat-field">
                <span className="echat-field__label">Замечания к поставке</span>
                <textarea
                  className="echat-field__input"
                  rows={4}
                  maxLength={500}
                  value={disputeComment}
                  onChange={(event) => onDisputeCommentChange(event.target.value)}
                  placeholder="Что не так: недовоз, брак, неверная позиция…"
                />
              </label>
            ) : null}
          </article>
        ) : null}

        {error ? (
          <p className="echat-error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="echat-actions">
          {substitutionProposed ? (
            excludeMode ? (
              <>
                <button
                  type="button"
                  className="echat-btn echat-btn--primary"
                  disabled={actionLoading || excludedLineIds.length === 0}
                  onClick={onExcludeLines}
                >
                  Продолжить без выбранных позиций
                </button>
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost"
                  disabled={actionLoading}
                  onClick={onCancelExcludeMode}
                >
                  Отмена
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="echat-btn echat-btn--primary"
                  disabled={actionLoading}
                  onClick={onAcceptSubstitution}
                >
                  Принять замену
                </button>
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost"
                  disabled={actionLoading || rfqActiveLines.length < 2}
                  onClick={onStartExcludeMode}
                >
                  Исключить позиции и продолжить
                </button>
                <button
                  type="button"
                  className="echat-btn echat-btn--danger"
                  disabled={actionLoading}
                  onClick={onCancelSubstitution}
                >
                  Отказаться от заявки
                </button>
              </>
            )
          ) : null}

          {awaitingConfirmation ? (
            disputeOpen ? (
              <>
                <button
                  type="button"
                  className="echat-btn echat-btn--danger"
                  disabled={actionLoading}
                  onClick={onSubmitDispute}
                >
                  Отправить замечания
                </button>
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost"
                  disabled={actionLoading}
                  onClick={onCancelDispute}
                >
                  Отмена
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="echat-btn echat-btn--primary"
                  disabled={actionLoading}
                  onClick={onConfirmCompletion}
                >
                  Да, всё получено
                </button>
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost"
                  disabled={actionLoading}
                  onClick={onStartDispute}
                >
                  Есть замечания
                </button>
              </>
            )
          ) : null}

          <button type="button" className="echat-btn echat-btn--primary" disabled={actionLoading} onClick={onOpenChat}>
            Написать поставщику
          </button>

          {canCancel ? (
            <button
              type="button"
              className="echat-btn echat-btn--danger"
              disabled={actionLoading}
              onClick={onCancelOrder}
            >
              Отменить заявку
            </button>
          ) : null}
        </div>
      </section>
    </>
  )
}
